import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  FaShoppingCart, FaUser, FaSearch, FaBars, FaTimes,
  FaChevronDown, FaChevronRight, FaSignOutAlt, FaCog,
  FaHome, FaShoppingBag, FaInfoCircle, FaEnvelope,
  FaInstagram, FaFacebook, FaTiktok, FaPercent,
  FaPhoneAlt, FaConciergeBell, FaHeart, FaBoxOpen,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSiteContent } from '../hooks/useSiteContent';
import { getIcon } from '../data/services';
import logo from '../assets/logo.jpeg';
import './Header.css';

const Header = () => {
  const [search, setSearch]               = useState('');
  const [isMenuOpen, setIsMenuOpen]       = useState(false);
  const [isUserOpen, setIsUserOpen]       = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);     // desktop dropdown
  const [mobileSvcOpen, setMobileSvcOpen]   = useState(false);     // drawer sublist
  const [scrolled, setScrolled]           = useState(false);
  const { currentUser, logout, isAdmin } = useAuth();
  const { content } = useSiteContent();
  const announcement = content.announcement;
  const { footer, contact } = content;
  const services = content.services || [];
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();

  const phone = footer?.phone || contact?.phone;
  const email = footer?.email || contact?.email;

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* close drawer/menus when clicking outside */
  useEffect(() => {
    if (!isMenuOpen && !isUserOpen && !isServicesOpen) return;
    const fn = (e) => {
      if (!e.target.closest('.mobile-drawer') && !e.target.closest('.menu-toggle'))
        setIsMenuOpen(false);
      if (!e.target.closest('.user-menu-wrap') && !e.target.closest('.user-menu-btn'))
        setIsUserOpen(false);
      if (!e.target.closest('.svc-nav-wrap'))
        setIsServicesOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [isMenuOpen, isUserOpen, isServicesOpen]);

  const navLinks = [
    { path: '/',         label: 'Home',    icon: <FaHome /> },
    { path: '/shop',     label: 'Shop',    icon: <FaShoppingBag /> },
    { path: '/about',    label: 'About',   icon: <FaInfoCircle /> },
    { path: '/contact',  label: 'Contact', icon: <FaEnvelope /> },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/shop?search=${encodeURIComponent(search)}`);
      setSearch('');
      setIsMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsUserOpen(false);
    navigate('/');
  };

  const close = () => {
    setIsMenuOpen(false);
    setIsUserOpen(false);
    setIsServicesOpen(false);
    setMobileSvcOpen(false);
  };

  return (
    <>
      {/* ── Utility bar: phone + email (CDW-style) ── */}
      <div className="utilbar">
        <div className="utilbar-inner">
          <span className="utilbar-tag">Your Home of Smart Solutions · across Kenya</span>
          <div className="utilbar-contact">
            {phone && (
              <a href={`tel:${phone.replace(/\s+/g, '')}`}><FaPhoneAlt /> {phone}</a>
            )}
            {email && (
              <a href={`mailto:${email}`}><FaEnvelope /> {email}</a>
            )}
          </div>
        </div>
      </div>

      {/* ── Announcement strip (editable in admin → Site Content) ── */}
      {announcement?.enabled !== false && announcement?.text && (
        <div className="topbar">
          <span>{announcement.text}</span>
        </div>
      )}

      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-inner">

          {/* ── LEFT: hamburger (mobile only) ── */}
          <button
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          {/* ── CENTER: logo ── */}
          <Link to="/" className="header-logo" onClick={close}>
            <img src={logo} alt="Follex Tech" className="logo-img" />
          </Link>

          {/* ── DESKTOP NAV (hidden on mobile) ── */}
          <nav className="desktop-nav">
            <NavLink to="/" end className={({ isActive }) => `dnav-link ${isActive ? 'active' : ''}`}>
              Home
            </NavLink>

            {/* Services mega-dropdown */}
            <div
              className="svc-nav-wrap"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                className={`dnav-link svc-nav-btn ${isServicesOpen ? 'active' : ''}`}
                onClick={() => navigate('/services')}
                aria-haspopup="true"
                aria-expanded={isServicesOpen}
              >
                Services <FaChevronDown className={`svc-nav-chev ${isServicesOpen ? 'flipped' : ''}`} />
              </button>

              {isServicesOpen && (
                <div className="svc-mega">
                  <div className="svc-mega-grid">
                    {services.map((s) => {
                      const Icon = getIcon(s.iconKey);
                      return (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          className="svc-mega-item"
                          onClick={close}
                        >
                          <span className="svc-mega-icon"><Icon /></span>
                          <span>
                            <strong>{s.title}</strong>
                            <small>{s.tagline}</small>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                  <Link to="/services" className="svc-mega-all" onClick={close}>
                    View all services <FaChevronRight />
                  </Link>
                </div>
              )}
            </div>

            {navLinks.filter((l) => l.path !== '/').map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                className={({ isActive }) => `dnav-link ${isActive ? 'active' : ''}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* ── RIGHT: search + icons ── */}
          <div className="header-right">
            {/* Desktop search */}
            <form className="hdr-search" onSubmit={handleSearch}>
              <FaSearch className="hdr-search-icon" />
              <input
                type="text"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </form>

            {/* Orders — works for guests (tracks orders from this device) */}
            <Link to="/orders" className="hdr-icon-btn hdr-orders-btn" title="Track my order" onClick={close}>
              <FaBoxOpen />
            </Link>

            {/* Wishlist */}
            <Link to="/wishlist" className="hdr-icon-btn hdr-wish-btn" title="Wishlist" onClick={close}>
              <FaHeart />
              {wishlistCount > 0 && (
                <span className="cart-badge">{wishlistCount > 9 ? '9+' : wishlistCount}</span>
              )}
            </Link>

            {/* Cart */}
            <Link to="/cart" className="hdr-icon-btn" title="Cart" onClick={close}>
              <FaShoppingCart />
              {cartCount > 0 && (
                <span className="cart-badge">{cartCount > 9 ? '9+' : cartCount}</span>
              )}
            </Link>

            {/* User */}
            <div className="user-menu-wrap">
              <button
                className="hdr-icon-btn user-menu-btn"
                onClick={() => setIsUserOpen(!isUserOpen)}
                aria-label="User menu"
              >
                {currentUser
                  ? <span className="avatar-circle">{currentUser.name?.charAt(0).toUpperCase() || 'U'}</span>
                  : <FaUser />}
                <FaChevronDown className={`chevron ${isUserOpen ? 'flipped' : ''}`} />
              </button>

              {isUserOpen && (
                <div className="user-dropdown">
                  {currentUser ? (
                    <>
                      <div className="ud-header">
                        <span className="ud-avatar">{currentUser.name?.charAt(0).toUpperCase() || 'U'}</span>
                        <div>
                          <strong>{currentUser.name || 'User'}</strong>
                          <small>{currentUser.email}</small>
                          {isAdmin && <span className="admin-pill">Admin</span>}
                        </div>
                      </div>
                      <div className="ud-body">
                        <Link to="/account" className="ud-item" onClick={close}><FaUser /> My Account</Link>
                        <Link to="/orders"  className="ud-item" onClick={close}><FaShoppingBag /> My Orders</Link>
                        <Link to="/wishlist" className="ud-item" onClick={close}><FaHeart /> My Wishlist</Link>
                        {isAdmin && (
                          <Link to="/admin" className="ud-item ud-admin" onClick={close}><FaCog /> Admin Dashboard</Link>
                        )}
                        <hr className="ud-divider" />
                        <button className="ud-item ud-danger" onClick={handleLogout}><FaSignOutAlt /> Log Out</button>
                      </div>
                    </>
                  ) : (
                    <div className="ud-body">
                      <Link to="/login"    className="ud-item" onClick={close}><FaUser /> Sign In</Link>
                      {/* Guests can track orders placed on this device */}
                      <Link to="/orders"   className="ud-item" onClick={close}><FaShoppingBag /> Track My Order</Link>
                      <Link to="/wishlist" className="ud-item" onClick={close}><FaHeart /> My Wishlist</Link>
                      <Link to="/register" className="ud-item ud-register" onClick={close}>Create Account</Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Mobile search bar (below header row) ── */}
        <div className="mobile-search-bar">
          <form onSubmit={handleSearch}>
            <FaSearch />
            <input
              type="text"
              placeholder="Search products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </form>
        </div>
      </header>

      {/* ── Mobile slide-out drawer ── */}
      <div className={`drawer-overlay ${isMenuOpen ? 'visible' : ''}`} onClick={() => setIsMenuOpen(false)} />
      <nav className={`mobile-drawer ${isMenuOpen ? 'open' : ''}`}>
        <div className="drawer-head">
          <img src={logo} alt="Follex Tech" className="drawer-logo" />
          <button className="drawer-close" onClick={() => setIsMenuOpen(false)} aria-label="Close menu">
            <FaTimes />
          </button>
        </div>

        <ul className="drawer-nav">
          <li>
            <NavLink to="/" end className={({ isActive }) => `drawer-link ${isActive ? 'active' : ''}`} onClick={() => setIsMenuOpen(false)}>
              <span className="drawer-link-icon"><FaHome /></span>
              Home
              <FaChevronRight className="drawer-chevron" />
            </NavLink>
          </li>

          {/* Services with expandable sublist */}
          <li>
            <button
              className="drawer-link drawer-svc-toggle"
              onClick={() => setMobileSvcOpen((v) => !v)}
              aria-expanded={mobileSvcOpen}
            >
              <span className="drawer-link-icon"><FaConciergeBell /></span>
              Services
              <FaChevronDown className={`drawer-chevron ${mobileSvcOpen ? 'flipped' : ''}`} />
            </button>
            {mobileSvcOpen && (
              <ul className="drawer-sublist">
                <li>
                  <Link to="/services" className="drawer-sublink" onClick={() => setIsMenuOpen(false)}>
                    All Services
                  </Link>
                </li>
                {services.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="drawer-sublink" onClick={() => setIsMenuOpen(false)}>
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>

          {navLinks.filter((l) => l.path !== '/').map((l) => (
            <li key={l.path}>
              <NavLink
                to={l.path}
                className={({ isActive }) => `drawer-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="drawer-link-icon">{l.icon}</span>
                {l.label}
                <FaChevronRight className="drawer-chevron" />
              </NavLink>
            </li>
          ))}

          {/* Order tracking — open to guests too */}
          <li>
            <NavLink
              to="/orders"
              className={({ isActive }) => `drawer-link ${isActive ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="drawer-link-icon"><FaBoxOpen /></span>
              Track My Order
              <FaChevronRight className="drawer-chevron" />
            </NavLink>
          </li>
        </ul>

        <div className="drawer-foot">
          <Link
            to="/contact"
            className="drawer-promo"
            onClick={() => setIsMenuOpen(false)}
          >
            <div>
              <strong>Request a Quote ⚡</strong>
              <span>Free consultation for your project</span>
            </div>
            <FaPercent />
          </Link>

          <Link to="/wishlist" className="drawer-cta drawer-cta-alt" onClick={() => setIsMenuOpen(false)}>
            <FaHeart /> My Wishlist{wishlistCount > 0 ? ` (${wishlistCount})` : ''}
          </Link>

          {currentUser ? (
            <Link to="/account" className="drawer-cta" onClick={() => setIsMenuOpen(false)}>
              <FaUser /> My Account
            </Link>
          ) : (
            <Link to="/login" className="drawer-cta" onClick={() => setIsMenuOpen(false)}>
              <FaUser /> Sign In
            </Link>
          )}

          <div className="drawer-social">
            <a href="https://instagram.com" aria-label="Instagram"><FaInstagram /></a>
            <a href="https://facebook.com"  aria-label="Facebook"><FaFacebook /></a>
            <a href="https://tiktok.com"    aria-label="TikTok"><FaTiktok /></a>
          </div>
        </div>
      </nav>

      {isUserOpen && (
        <div className="overlay" onClick={() => setIsUserOpen(false)} />
      )}
    </>
  );
};

export default Header;
